import { Image as ImageIcon } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { type TransformParams } from "@/lib/images";
import { photoCaption, useImageFallback } from "./media-helpers";
import type { MediaItem } from "@adva/types";

/**
 * The shared image cell used by the gallery grid (and, post-tracer-bullet, the
 * Overview preview strip). Owns the §I7 fallback chain, lazy loading, the
 * caption line below the image (§I3.6 — never overlaid), and the truncated-
 * caption Tooltip (§I4.2, 150ms). When `onOpen` is set the tile is a button
 * that opens the lightbox at this photo's flat index.
 */
export function JobPhoto({
  item,
  slot,
  onOpen,
  className,
  hideCaption = false,
}: {
  item: MediaItem;
  slot: TransformParams;
  onOpen?: () => void;
  className?: string;
  /** Suppress the visible caption line (e.g. the compact Overview preview
   * strip, where the fixed ~110px footprint has no room for it). The hover
   * Tooltip + the button's aria-label still carry the caption (§3.1). */
  hideCaption?: boolean;
}) {
  const { src, onError } = useImageFallback(item.public_url, slot);
  const caption = photoCaption(item);

  const frame = (
    <div
      className={cn(
        "relative aspect-[3/2] w-full overflow-hidden rounded-[var(--radius-md)] border border-[color:var(--color-border)] bg-[color:var(--color-surface)]",
        onOpen &&
          "transition-shadow duration-150 group-hover:ring-2 group-hover:ring-[color:var(--color-accent)] group-focus-visible:ring-2 group-focus-visible:ring-[color:var(--color-accent)]",
      )}
    >
      {src ? (
        <img
          src={src}
          alt={caption ?? ""}
          loading="lazy"
          onError={onError}
          className="size-full object-cover"
        />
      ) : (
        // §I7.1 step 3: "this slot held an image." Glyph at ~60% of the slot,
        // text-lo on surface. The aspect box holds at every step → zero CLS
        // (§I7.3).
        <div className="flex size-full items-center justify-center">
          <ImageIcon
            className="size-[60%] text-[color:var(--color-text-lo)]"
            aria-hidden
          />
        </div>
      )}
    </div>
  );

  // Caption: label SIZE via raw token vars (Amendment B fail-open), normal
  // case — explicitly NOT `.typo-label`, whose uppercase+tracking would shout
  // prose (§I4.2). Rendered verbatim, never decorated (§I4.5).
  const captionLine =
    caption && !hideCaption ? (
      <span className="mt-1 block truncate font-[family-name:var(--font-family-body)] text-[length:var(--font-size-label)] leading-[var(--line-height-label)] text-[color:var(--color-text-md)]">
        {caption}
      </span>
    ) : null;

  if (!onOpen) {
    return (
      <div className={cn("min-w-0", className)}>
        {frame}
        {captionLine}
      </div>
    );
  }

  const tile = (
    <button
      type="button"
      onClick={onOpen}
      aria-label={caption ?? "Open photo"}
      className={cn(
        "group min-w-0 text-left focus:outline-none focus-visible:outline-none",
        className,
      )}
    >
      {frame}
      {captionLine}
    </button>
  );

  // Additive enrichment only (§3.1): hover reveals the full caption; the
  // button's aria-label already carries it for keyboard/AT.
  if (!caption) return tile;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{tile}</TooltipTrigger>
      <TooltipContent>{caption}</TooltipContent>
    </Tooltip>
  );
}
